import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Empty, Spinner } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, caseStatusLabel } from "@/lib/format";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Saker() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const load = async () => {
    setItems(null);
    const params = new URLSearchParams({ limit: "200" });
    if (status) params.set("status", status);
    const { data } = await api.get(`/cases?${params}`);
    setItems(data.items);
  };
  useEffect(() => { load(); }, [status]);

  return (
    <div data-testid="saker-page">
      <PageHeader
        title="Saker"
        subtitle="Kundeservice og saksbehandling"
        actions={
          <button onClick={() => setOpen(true)}
            className="h-9 px-3.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2"
            data-testid="ny-sak-btn">
            <Plus className="h-4 w-4" /> Ny sak
          </button>
        }
      />

      <div className="flex items-center gap-1 bg-zinc-900/60 rounded-md p-1 border border-zinc-900 mb-4 w-fit">
        {["", "ny", "under_arbeid", "venter_kunde", "lost", "lukket"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded text-xs transition-colors ${status === s ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200"}`}>
            {s ? caseStatusLabel(s) : "Alle"}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {items === null ? <div className="p-8"><Spinner /></div>
          : items.length === 0 ? <Empty title="Ingen saker" />
          : (
          <div className="divide-y divide-zinc-900">
            {items.map((c) => (
              <div key={c.id} className="p-4 hover:bg-zinc-900/30 transition-colors" data-testid={`sak-row-${c.case_number}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-emerald-400">{c.case_number}</span>
                      <span className="text-zinc-100 font-medium">{c.subject}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {c.customer?.full_name || "—"} · {c.category} · {fmtDate(c.created_at)}
                    </div>
                    {c.description && <div className="text-xs text-zinc-400 mt-2 line-clamp-2">{c.description}</div>}
                  </div>
                  <div className="flex gap-2">
                    <StatusBadge value={c.priority} />
                    <StatusBadge value={c.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <NySakDialog open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); }} />
    </div>
  );
}

function NySakDialog({ open, onOpenChange, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", subject: "", description: "", category: "generell", priority: "normal" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/customers?limit=200").then(({ data }) => setCustomers(data.items || []));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.customer_id) delete payload.customer_id;
      await api.post("/cases", payload);
      toast.success("Sak opprettet");
      onCreated();
    } catch (e) { toast.error("Kunne ikke opprette sak"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900">
        <DialogHeader>
          <DialogTitle className="font-heading">Ny sak</DialogTitle>
          <DialogDescription className="sr-only">Registrer en ny henvendelse</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase text-zinc-500 mb-1">Kunde (valgfritt)</label>
            <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm">
              <option value="">— ingen —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_number} · {c.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase text-zinc-500 mb-1">Emne</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              required className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] uppercase text-zinc-500 mb-1">Beskrivelse</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1">Kategori</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm">
                {["generell", "faktura", "forbruk", "avtale", "teknisk", "betaling"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1">Prioritet</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm">
                {["lav", "normal", "hoy", "kritisk"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" disabled={busy}
            className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Opprett sak
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
